import { ConfigState } from '@/store/configStore';
import { deepMerge } from '@/utils/deepMerge';

export const negativeFeedbackOptions = {
  en: [
    'Not factually correct',
    'Unclear response',
    'Missing info',
    'Offensive / Unsafe',
    'Suggestion',
  ],
  de: ['Nicht faktualisiert', 'Unklar', 'Info fehlt', 'Offensiv / Unsicher', 'Vorschlag'],
  fr: [
    'Non factuel',
    'Réponse ambiguë',
    'Information manquante',
    'Offensif / Inapproprié',
    'Suggestion',
  ],
  nl: [
    'Niet factuur',
    'Onduidelijk antwoord',
    'Ontbrekende info',
    'Offensief / Onveilig',
    'Voorstel',
  ],
} as const;

export const DEFAULT_CONFIG_EN: Partial<ConfigState['config']> = {
  ui: {
    header: {
      title: 'Chat with me',
      supportiveText: 'Supportive text',
    },
    input: {
      placeholder: 'Your message',
    },
    feedback: {
      thanks: 'Thanks! We appreciate your feedback',
      whatWentWrong: 'What went wrong?',
    },
    emptyScreen: {
      title: 'Your guide through our knowledge base',
      description: `Looking for information, insights, or answers? Ask me anything and I'll help you find what you need.`,
      question: 'How can we help you?',
    },
    inactivity: {
      warningMessage:
        'Are you still there? This chat will reset if you are inactive for 10 minutes. Let me know if you want to keep the conversation going.',
      resetMessage: 'We reset the chat so you can start fresh.',
    },
  },
};

export const DEFAULT_CONFIG_DE: Partial<ConfigState['config']> = {
  ui: {
    header: {
      title: 'Stell mir Deine Fragen',
      supportiveText: 'Ich bin hier, um Dir zu helfen',
    },
    input: {
      placeholder: 'Deine Nachricht',
    },
    feedback: {
      thanks: 'Danke für Deine Unterstützung!',
      whatWentWrong: 'Bitte konkretisiere Dein Feedback.',
    },
    emptyScreen: {
      title: 'Ihr Kompass durch unseren Wissensspeicher',
      description: `Sie suchen nach Informationen, Erkenntnissen oder Antworten? Stellen Sie mir eine Frage und ich helfe Ihnen gerne weiter.`,
      question: 'Wie können wir Ihnen helfen?',
    },
    inactivity: {
      warningMessage:
        'Bist du noch da? Dieser Chat wird nach 10 Minuten Inaktivität zurückgesetzt. Sag mir Bescheid, wenn wir weitermachen sollen.',
      resetMessage: 'Wir starten eine neue Unterhaltung für dich.',
    },
  },
};

export const DEFAULT_CONFIG_FR: Partial<ConfigState['config']> = {
  ui: {
    header: {
      title: 'Discutez avec moi',
      supportiveText: 'Texte de soutien',
    },
    input: {
      placeholder: 'Votre message',
    },
    feedback: {
      thanks: 'Merci ! Nous apprécions vos commentaires',
      whatWentWrong: "Qu'est-ce qui n'a pas fonctionné ?",
    },
    emptyScreen: {
      title: 'Votre guide à travers notre base de connaissances',
      description: `Vous cherchez des informations, des insights ou des réponses ? Posez-moi n'importe quelle question et je vous aiderai à trouver ce dont vous avez besoin.`,
      question: 'Comment pouvons-nous vous aider ?',
    },
    inactivity: {
      warningMessage:
        'Êtes-vous toujours là ? Ce chat sera réinitialisé si vous êtes inactif pendant 10 minutes. Faites-moi savoir si vous souhaitez continuer la conversation.',
      resetMessage: 'Nous avons réinitialisé le chat pour que vous puissiez recommencer.',
    },
  },
};

export const DEFAULT_CONFIG_NL: Partial<ConfigState['config']> = {
  ui: {
    header: {
      title: 'Chat met mij',
      supportiveText: 'Ondersteunende tekst',
    },
    input: {
      placeholder: 'Uw bericht',
    },
    feedback: {
      thanks: 'Bedankt! We waarderen uw feedback',
      whatWentWrong: 'Wat ging er mis?',
    },
    emptyScreen: {
      title: 'Uw gids door onze kennisbank',
      description: `Zoekt u informatie, inzichten of antwoorden? Stel me gerust een vraag en ik help u vinden wat u nodig heeft.`,
      question: 'Hoe kunnen we u helpen?',
    },
    inactivity: {
      warningMessage:
        'Bent u er nog? Deze chat wordt gereset als u 10 minuten inactief bent. Laat het me weten als u het gesprek wilt voortzetten.',
      resetMessage: 'We hebben de chat gereset zodat u opnieuw kunt beginnen.',
    },
  },
};

export const DEFAULT_CONFIG: ConfigState['config'] = {
  webhookUrl: '',
  cssUrl: '',
  requestHeaders: {},
  language: 'en',
  colors: {
    accent: {
      25: '#EDFBFF',
      100: '#D9F6FF',
      200: '#6DDBFF',
      300: '#47D1FF',
      400: '#1AC2FA',
      500: '#00ACE5',
      600: '#0890BF',
      700: '#097198',
      800: '#00597F',
      900: '#004765',
    },
  },
  ui: {
    header: {
      haveTitle: true,
      haveIcon: true,
      haveFullScreenMode: true,
      haveSupportiveText: true,
      haveRestartButton: false,
      haveSettingsButton: false,
    },
    generalSpecificVariant: 'default',
    watermark: 'default',
    fullScreenModeByDefault: false,
    floatingButton: {
      rounded: 'full',
    },
    input: {
      sendButtonVariant: 'primary',
    },
    gdpr: {
      enabled: true,
    },
    emptyScreen: {
      icon: false,
    },
    inactivity: {
      enabled: false,
      timeout: 10 * 60 * 1000, // 10 minutes
      warningLead: 1 * 60 * 1000, // 1 minute
    },
  },
};

export const getConfig = (language: string) => {
  if (language === 'de') {
    return deepMerge(DEFAULT_CONFIG, DEFAULT_CONFIG_DE);
  }
  if (language === 'fr') {
    return deepMerge(DEFAULT_CONFIG, DEFAULT_CONFIG_FR);
  }
  if (language === 'nl') {
    return deepMerge(DEFAULT_CONFIG, DEFAULT_CONFIG_NL);
  }
  return deepMerge(DEFAULT_CONFIG, DEFAULT_CONFIG_EN);
};
